import * as React from 'react';
import { Button, Table, TextField } from '../common';
import { Grid2 as Grid, Tab, Tabs, Typography } from '@mui/material';
import { fontFamily } from '../../utils';
import { BaseDialog, BaseDialogProps, BaseDialogState } from '../common/dialog/BaseDialog';
import { getThemeMode } from '../../reducers';

interface Props extends BaseDialogProps {
    readonly jobs: Entities.CloudBuildJob[];
    readonly onClose: () => void;
    readonly onStartBuild: (jobs: Entities.CloudBuildJob[], clean: boolean, branch: string) => void;
}

interface State extends BaseDialogState {
    readonly selected: Entities.CloudBuildJob[];
    readonly tabSelected: number;
    readonly branch: string;
}

export class StartBuildDialog extends BaseDialog<Props, State> {
    state = this.initState();

    protected renderTitle(themeMode: ThemeMode): React.JSX.Element {
        return (
            <>
                <div>
                    <Typography variant={'h5'}>
                        Build targets
                    </Typography>
                    <p style={{ fontSize: '0.875rem', fontWeight: 400, lineHeight: '1.25rem', letterSpacing: '-0.006rem' }}>
                        Select the target(s) you want to build.
                    </p>
                </div>
            </>
        );
    }

    protected renderContent = () => {
        const { tabSelected } = this.state;
        const changeSubTab = (event: any, value: any) => {
            this.setState({ tabSelected: value })
        }
        const themeMode = getThemeMode();

        return (
            <>
                <Tabs
                    value={tabSelected}
                    onChange={changeSubTab}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    allowScrollButtonsMobile={true}
                    scrollButtons="auto"
                    style={{
                        borderBottom: themeMode === 'light' ? '1px solid #e5e7e9' : '1px solid #26272b',
                    }}
                    sx={{ display: { xs: 'none', sm: 'block' } }}
                >
                    <Tab
                        key={`tab_0`}
                        disableRipple={true}
                        label={'Selection'}
                        value={0}
                    />
                    <Tab
                        key={`tab_1`}
                        disableRipple={true}
                        label={'Overrides (optional)'}
                        value={1}
                    />
                </Tabs>
                {this.renderTabSelection()}
            </>
        );
    };

    private renderTabSelection = () => {
        const { jobs } = this.props;
        const { tabSelected, branch } = this.state;
        if (tabSelected === 0) {
            const columns = [
                { title: 'Build Name', field: 'name', cellStyle: { padding: '10px 16px', fontFamily, fontSize: 14 }},
            ];

            return (
                <Table
                    styled={this.props.styled}
                    data={jobs}
                    columns={columns}
                    showBox={false}
                    options={{
                        showTitle: false,
                        selection: true,
                        search: false,
                        sorting: false,
                        draggable: false,
                        paging: false,
                        headerStyle: {
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            lineHeight: '1.3',
                            padding: '10px 16px',
                        }

                    }}
                    onSelectionChange={this.onSelectionChange}
                    ignoreSmall={true}
                />
            );
        } else {
            const updateBranch = (branch: string) => this.setState({ branch });
            return (
                <Grid container={true} spacing={0}>
                    <Grid size={12}>
                        <TextField
                            label={'Branch'}
                            fullWidth={true}
                            value={branch}
                            onChange={updateBranch}
                            variant="outlined"
                        />
                    </Grid>
                </Grid>
            );
        }
    }

    private onSelectionChange = (rows: any) => this.setState({ selected: rows });

    protected renderActions = () => {
        return (
            <>
                <Button text="Clean Build" onClick={this.onSubmitClean} variant="contained" disabled={!this.validate()} />
                <Button text="Build" onClick={this.onSubmit} variant="contained" disabled={!this.validate()} />
            </>
        );
    };

    private onSubmit = () => {
        const { selected, branch } = this.state;
        if (!this.validate()) {
            return;
        }

        this.props.onStartBuild(selected, false, branch);
        this.onClose();
    };

    private onSubmitClean  = () => {
        const { selected, branch } = this.state;
        if (!this.validate()) {
            return;
        }

        this.props.onStartBuild(selected, true, branch);
        this.onClose();
    };

    private validate(): boolean {
        const { selected } = this.state;
        return selected.length > 0;
    }

    protected initState(): State {
        return {
            selected: [],
            tabSelected: 0,
            branch: '',
        };
    }
}
